import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useMemo, useState } from "react";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Card from "react-bootstrap/Card";
import { Controller, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import Select from "react-select";
import styled from "styled-components";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import ContentWrapper from "../../components/content-wrapper";
import { useProperty } from "../../components/property-provider";
import { SectionHeading } from "../../components/section-heading";
import { StyledSpinner } from "../ariSnapshot/styles";
import { regenPropertyCache } from "../common/api";
import {
  BaseAccommodationType,
  OccupancyEntity,
  OccupationFilterType,
  OccupationType,
} from "./domain/Occupancy.entity";
import { useOccupancyApi } from "./infrastructure/api/occupancy.api";
import { occupationTypesErrorMessages, occupationTypesMessages } from "./messages";
import { OccupancyFormData, OccupancyFormInput, occupancyFormSchema } from "./schema";
import { usePropertyConfigurationApi } from "./infrastructure/api/propertyConfiguration.api";
import { useOccupations } from "./infrastructure/hooks/occupations.hook";
import { useApiClient } from "../../lib/http";

type GrpcErrorMetadata = {
  "error-code"?: string;
  "error-message"?: string;
};

const CHILDREN_TYPES = [
  OccupationFilterType.FOR_ADULTS_CHILDREN,
  OccupationFilterType.FOR_ADULTS_CHILDREN_BABIES,
  OccupationFilterType.FOR_ADULTS_TEENS_CHILDREN,
  OccupationFilterType.FOR_ADULTS_TEENS_CHILDREN_BABIES,
  OccupationFilterType.FOR_SENIORS_ADULTS_TEENS_CHILDREN_BABIES,
];

const BABIES_TYPES = [
  OccupationFilterType.FOR_ADULTS_CHILDREN_BABIES,
  OccupationFilterType.FOR_ADULTS_TEENS_CHILDREN_BABIES,
  OccupationFilterType.FOR_SENIORS_ADULTS_TEENS_CHILDREN_BABIES,
];

const TEENS_TYPES = [
  OccupationFilterType.FOR_ADULTS_TEENS_CHILDREN,
  OccupationFilterType.FOR_ADULTS_TEENS_CHILDREN_BABIES,
  OccupationFilterType.FOR_SENIORS_ADULTS_TEENS_CHILDREN_BABIES,
];

const SENIORS_TYPES = [OccupationFilterType.FOR_SENIORS_ADULTS_TEENS_CHILDREN_BABIES];

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

function OccupancyForm() {
  const [showAlert, setShowAlert] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<{
    type: "SUCCESS" | "ERROR";
    errorCode?: string;
    message?: string;
  }>({ type: "SUCCESS" });

  const intl = useIntl();
  const propertyInfo = useProperty();
  const apiClient = useApiClient();
  const { updateOccupationTypes } = useOccupancyApi();
  const { getOccupancyOptions } = usePropertyConfigurationApi();
  const {
    occupationTypeOptions,
    occupationFilterOptions,
    baseAccommodationTypeOptions,
    getOccupationTypeSelected,
    getOccupationFilterTypeSelected,
    getBaseAccommodationTypeSelected,
  } = useOccupations();

  const {
    register,
    watch,
    handleSubmit,
    control,
    reset,
    formState: { errors },
    setValue,
  } = useForm<OccupancyFormInput, unknown, OccupancyFormData>({
    resolver: zodResolver(occupancyFormSchema(intl)),
  });

  const onSubmit = useCallback(
    async (data: OccupancyFormData) => {
      const occupancy = OccupancyEntity.of({
        occupationType: data.occupationType.value as OccupationType,
        occupationFilterType: data.occupationFilterType.value as OccupationFilterType,
        minChildrenAge: data.minChildrenAge,
        maxChildrenAge: data.maxChildrenAge,
        minBabiesAge: data.minBabiesAge,
        maxTeensAge: data.maxTeensAge,
        maxSeniorsAge: data.maxSeniorsAge,
        filterByAge: data.filterByAge,
        baseAccommodationType: data.baseAccommodationType.value as BaseAccommodationType,
      });

      try {
        setLoading(true);
        await updateOccupationTypes(occupancy);
        await regenPropertyCache(apiClient, propertyInfo.ticker);
        setAlert({ type: "SUCCESS" });
      } catch (e) {
        if (
          e &&
          typeof e === "object" &&
          "metadata" in e &&
          e.metadata &&
          typeof e.metadata === "object"
        ) {
          const metadata = e.metadata as GrpcErrorMetadata;
          setAlert({
            type: "ERROR",
            errorCode: metadata["error-code"],
            message: metadata["error-message"],
          });
        } else {
          setAlert({ type: "ERROR" });
        }
      } finally {
        setShowAlert(true);
        setLoading(false);
        setTimeout(() => setShowAlert(false), 3000);
      }
    },
    [apiClient, propertyInfo.ticker, updateOccupationTypes]
  );

  const fetchOccupancy = useCallback(async () => {
    const occupancy = await getOccupancyOptions();
    const formData = {
      ...occupancy,
      occupationType: getOccupationTypeSelected(occupancy.occupationType),
      occupationFilterType: getOccupationFilterTypeSelected(occupancy.occupationFilterType),
      baseAccommodationType: getBaseAccommodationTypeSelected(occupancy.baseAccommodationType),
    };
    reset(formData);
  }, [
    getOccupancyOptions,
    getOccupationTypeSelected,
    getOccupationFilterTypeSelected,
    getBaseAccommodationTypeSelected,
    reset,
  ]);

  const alertType = useMemo(() => {
    switch (alert.type) {
      case "ERROR":
        return "danger";
      default:
      case "SUCCESS":
        return "success";
    }
  }, [alert.type]);

  const errorMessage = useMemo(() => {
    switch (alert.errorCode) {
      case "OCCUPATION_LINES_IN_USE":
        return occupationTypesErrorMessages.OCCUPATION_LINES_IN_USE;
      default:
        return occupationTypesMessages.saveError;
    }
  }, [alert.errorCode]);

  useEffect(() => {
    setLoading(true);
    fetchOccupancy().then(() => setLoading(false));
  }, [fetchOccupancy]);

  const occupationFilterType =
    (watch("occupationFilterType")?.value as OccupationFilterType) ??
    OccupationFilterType.NO_FILTER;

  useEffect(() => {
    if (!CHILDREN_TYPES.includes(occupationFilterType)) {
      setValue("minChildrenAge", null);
      setValue("maxChildrenAge", null);
    } else {
      setValue("minChildrenAge", watch("minChildrenAge") ?? 0);
      setValue("maxChildrenAge", watch("maxChildrenAge") ?? 0);
    }

    if (!BABIES_TYPES.includes(occupationFilterType)) {
      setValue("minBabiesAge", null);
    } else {
      setValue("minBabiesAge", watch("minBabiesAge") ?? 0);
    }

    if (!TEENS_TYPES.includes(occupationFilterType)) {
      setValue("maxTeensAge", null);
    } else {
      setValue("maxTeensAge", watch("maxTeensAge") ?? 0);
    }

    if (!SENIORS_TYPES.includes(occupationFilterType)) {
      setValue("maxSeniorsAge", null);
    } else {
      setValue("maxSeniorsAge", watch("maxSeniorsAge") ?? 0);
    }
  }, [occupationFilterType, setValue, watch]);

  return (
    <ContentWrapper>
      <SectionHeading>
        <FormattedMessage {...occupationTypesMessages.title} />
      </SectionHeading>
      <Card className="ml-4 mr-4 mb-4 mt-4">
        <Card.Body>
          <Form onSubmit={handleSubmit(onSubmit)} className="w-[320px]">
            <Form.Group>
              <Form.Label htmlFor="occupationType">
                <FormattedMessage {...occupationTypesMessages.occupationType} />
              </Form.Label>
              <Controller
                name="occupationType"
                control={control}
                render={({ field }) => <Select {...field} options={occupationTypeOptions} />}
              />
            </Form.Group>

            <Form.Group>
              <Form.Label htmlFor="occupationFilterType">
                <FormattedMessage {...occupationTypesMessages.occupancyFilterType} />
              </Form.Label>
              <Controller
                name="occupationFilterType"
                control={control}
                render={({ field }) => <Select {...field} options={occupationFilterOptions} />}
              />
            </Form.Group>

            <Form.Group>
              <Form.Label htmlFor="baseAccommodationType">
                <FormattedMessage {...occupationTypesMessages.baseAccommodationType} />
              </Form.Label>
              <Controller
                name="baseAccommodationType"
                control={control}
                render={({ field }) => <Select {...field} options={baseAccommodationTypeOptions} />}
              />
            </Form.Group>

            {CHILDREN_TYPES.includes(occupationFilterType) && (
              <>
                <Form.Group>
                  <Form.Label htmlFor="minChildrenAge">
                    <FormattedMessage {...occupationTypesMessages.minChildrenAge} />
                  </Form.Label>
                  <Form.Control
                    id="minChildrenAge"
                    {...register("minChildrenAge")}
                    type="number"
                    isInvalid={errors.minChildrenAge != null}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label htmlFor="maxChildrenAge">
                    <FormattedMessage {...occupationTypesMessages.maxChildrenAge} />
                  </Form.Label>
                  <Form.Control
                    id="maxChildrenAge"
                    {...register("maxChildrenAge")}
                    type="number"
                    isInvalid={errors.maxChildrenAge != null}
                  />
                </Form.Group>
              </>
            )}
            {errors.maxChildrenAge && (
              <span className="text-red-500">{errors.maxChildrenAge.message}</span>
            )}
            {errors.minChildrenAge && (
              <span className="text-red-500">{errors.minChildrenAge.message}</span>
            )}

            {BABIES_TYPES.includes(occupationFilterType) && (
              <Form.Group>
                <Form.Label htmlFor="minBabiesAge">
                  <FormattedMessage {...occupationTypesMessages.minBabiesAge} />
                </Form.Label>
                <Form.Control
                  id="minBabiesAge"
                  {...register("minBabiesAge")}
                  type="number"
                  isInvalid={errors.minBabiesAge != null}
                />
              </Form.Group>
            )}
            {errors.minBabiesAge && (
              <span className="text-red-500">{errors.minBabiesAge.message}</span>
            )}

            {TEENS_TYPES.includes(occupationFilterType) && (
              <Form.Group>
                <Form.Label htmlFor="maxTeensAge">
                  <FormattedMessage {...occupationTypesMessages.maxTeensAge} />
                </Form.Label>
                <Form.Control
                  id="maxTeensAge"
                  {...register("maxTeensAge")}
                  type="number"
                  isInvalid={errors.maxTeensAge != null}
                />
              </Form.Group>
            )}
            {errors.maxTeensAge && (
              <span className="text-red-500">{errors.maxTeensAge.message}</span>
            )}

            {SENIORS_TYPES.includes(occupationFilterType) && (
              <Form.Group>
                <Form.Label htmlFor="maxSeniorsAge">
                  <FormattedMessage {...occupationTypesMessages.maxAdultsAge} />
                </Form.Label>
                <Form.Control
                  id="maxSeniorsAge"
                  {...register("maxSeniorsAge")}
                  type="number"
                  isInvalid={errors.maxSeniorsAge != null}
                />
              </Form.Group>
            )}
            {errors.maxSeniorsAge && (
              <span className="text-red-500">{errors.maxSeniorsAge.message}</span>
            )}

            <Form.Group>
              <Form.Check
                label={intl.formatMessage(occupationTypesMessages.filterGuestsByAge)}
                {...register("filterByAge")}
              />
            </Form.Group>

            <Button type="submit" disabled={loading}>
              <ButtonContainer>
                <FormattedMessage {...occupationTypesMessages.save} />
                {loading && <StyledSpinner className="fa fa-spinner fa-spin" />}
              </ButtonContainer>
            </Button>
          </Form>

          <Alert hidden={!showAlert} variant={alertType} className="w-fit mt-3 mb-0 self-center">
            {alert.type === "SUCCESS" ? (
              <FormattedMessage {...occupationTypesMessages.saveSuccess} />
            ) : (
              <FormattedMessage {...errorMessage} />
            )}
          </Alert>
        </Card.Body>
      </Card>
    </ContentWrapper>
  );
}

export default withAuthorizationVerifier(OccupancyForm);
